import type { ReactNode } from 'react'
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useRouteLoaderData,
  type LoaderFunctionArgs,
} from 'react-router'
import { publicConfig, siteUrl, type AppConfig } from '@/config'
import { loadSiteSettings, type SiteSettings } from '@/site/lib/siteSettings'
import './index.css'

/*
 * The document for every page. The customer site is server-rendered (src/site); the control panel
 * (src/admin) renders in the browser only (its root route has a hydrating clientLoader).
 */

export interface RootData {
  config: AppConfig
  siteUrl: string
  /** Theme + banner from the control panel. */
  site: SiteSettings
}

export async function loader({ request }: LoaderFunctionArgs): Promise<RootData> {
  return { config: publicConfig(), siteUrl: siteUrl(request), site: await loadSiteSettings() }
}

/** Nothing here changes between pages. */
export const shouldRevalidate = () => false

/** JSON for an inline <script>: `<` escaped so the data can't close the tag. */
const inlineJson = (value: unknown) => JSON.stringify(value).replace(/</g, '\\u003c')

export function Layout({ children }: { children: ReactNode }) {
  const data = useRouteLoaderData<typeof loader>('root')

  return (
    <html lang="en" className="scroll-smooth" data-theme={data?.site.theme ?? 'default'}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#ffffff" />
        <link rel="icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" href="/launcher-icon-4x.png" />
        <link rel="manifest" href="/manifest.json" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
        <Meta />
        <Links />
        {/* Read by src/config.ts in the browser; runs before the (deferred) app modules. */}
        {data && <script dangerouslySetInnerHTML={{ __html: `window.__QT_CONFIG__=${inlineJson(data.config)}` }} />}
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function Root() {
  return <Outlet />
}

/** Last resort (errors outside the site and admin boundaries). */
export function ErrorBoundary() {
  const error = useRouteError()
  const status = isRouteErrorResponse(error) ? error.status : 500
  if (!isRouteErrorResponse(error)) console.error(error)
  return (
    <main style={{ fontFamily: 'system-ui, sans-serif', padding: '4rem 1rem', textAlign: 'center' }}>
      <title>{status === 404 ? 'Page Not Found | Quilting Tutorials' : 'Something went wrong | Quilting Tutorials'}</title>
      <h1>{status === 404 ? 'Page not found' : 'Something went wrong'}</h1>
      <p>
        <a href="/">Back to Quilting Tutorials</a>
      </p>
    </main>
  )
}
